import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { SellerAccountClient, TimezoneInfo } from 'src/WebApiController';

export const useCustomTimeZoneSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();

  const { trackError, showErrorDialog } = useClientLogContext();

  const queryClient = useQueryClient();
  const queryKey = [
    'getCustomTimeZoneSettings',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const shouldQuery = useMemo(
    () => activeAccountWebClientConfig.activeAccountId != null,
    [activeAccountWebClientConfig.activeAccountId]
  );
  const query = useQuery({
    queryKey,
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getCustomTimeZoneSettings();
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('SellerAccountClient.getCustomTimeZoneSettings', error);
      },
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ input }: { input: TimezoneInfo | undefined }) => {
      return await new SellerAccountClient(
        activeAccountWebClientConfig
      ).mergeCustomTimeZoneSettings(input);
    },
    // optimistic ui
    onMutate: ({ input }) => {
      queryClient.cancelQueries({ queryKey });
      const prevValue = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, input ?? null);
      return { prevValue };
    },
    onError: (err: ErrorTypes, { input }, context) => {
      queryClient.setQueryData(queryKey, context?.prevValue ?? null);
      showErrorDialog('SellerAccountClient.mergeCustomTimeZoneSettings', err, {
        trackErrorData: { input },
      });
    },
  });

  return {
    customTimeZoneSettings: query.data,
    isLoading: !shouldQuery || query.isLoading, // if no active account, consider as loading
    mergeCustomTimeZoneSettings: mutation.mutateAsync,
  };
};
